// FutureTec-v2 i18n bootstrap.
// Phase 1.1 (2026-02) — 9 languages, landing page only.
// Language storage key: `futuretec_lang` (LocalStorage).
// Detection order: querystring `?lng` → localStorage → navigator → fallback `en`.
import i18n from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { initReactI18next } from "react-i18next";

import en    from "./locales/en/common.json";
import bn    from "./locales/bn/common.json";
import id    from "./locales/id/common.json";
import ms    from "./locales/ms/common.json";
import th    from "./locales/th/common.json";
import ta    from "./locales/ta/common.json";
import zhHant from "./locales/zh-Hant/common.json";
import si    from "./locales/si/common.json";
import ur    from "./locales/ur/common.json";

// Display order for the switcher — English first, then a stable order.
// (Nare: "however feels natural — English first is fine.")
export const SUPPORTED_LANGS = [
  { code: "en",      label: "English",           native: "English",           dir: "ltr" },
  { code: "bn",      label: "Bengali",           native: "বাংলা",              dir: "ltr" },
  { code: "id",      label: "Bahasa Indonesia",  native: "Bahasa Indonesia",  dir: "ltr" },
  { code: "ms",      label: "Bahasa Melayu",     native: "Bahasa Melayu",     dir: "ltr" },
  { code: "th",      label: "Thai",              native: "ภาษาไทย",           dir: "ltr" },
  { code: "ta",      label: "Tamil",             native: "தமிழ்",              dir: "ltr" },
  { code: "zh-Hant", label: "繁體中文",           native: "繁體中文",           dir: "ltr" },
  { code: "si",      label: "Sinhala",           native: "සිංහල",              dir: "ltr" },
  { code: "ur",      label: "Urdu",              native: "اردو",              dir: "rtl" },
];

export const LANG_STORAGE_KEY = "futuretec_lang";

// Fast lookup for direction. Kept as a Set so future RTL additions
// (Arabic, Hebrew, Farsi) are a single-line change.
const RTL_LANGS = new Set(
  SUPPORTED_LANGS.filter((l) => l.dir === "rtl").map((l) => l.code)
);

const resources = {
  en:        { common: en },
  bn:        { common: bn },
  id:        { common: id },
  ms:        { common: ms },
  th:        { common: th },
  ta:        { common: ta },
  "zh-Hant": { common: zhHant },
  si:        { common: si },
  ur:        { common: ur },
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: "en",
    supportedLngs: SUPPORTED_LANGS.map((l) => l.code),
    lowerCaseLng: false,           // preserve `zh-Hant` casing
    nonExplicitSupportedLngs: false,
    ns: ["common"],
    defaultNS: "common",
    interpolation: { escapeValue: false }, // React already escapes
    detection: {
      order: ["querystring", "localStorage", "navigator"],
      lookupQuerystring: "lng",
      lookupLocalStorage: LANG_STORAGE_KEY,
      caches: ["localStorage"],
    },
    react: {
      useSuspense: false,
    },
  });

// ---- Direction + <html lang> bootstrap ---- //
// Applies `dir="rtl"` and `lang="ur"` on <html> for Urdu, `ltr` otherwise.
// Set once on load and updated on every `languageChanged`.
if (typeof document !== "undefined") {
  const applyDirAndLang = (lng) => {
    const code = lng || i18n.language || "en";
    document.documentElement.dir = RTL_LANGS.has(code) ? "rtl" : "ltr";
    document.documentElement.lang = code;
  };
  i18n.on("languageChanged", applyDirAndLang);
  applyDirAndLang(i18n.language);
}

export default i18n;
